import { getMdtCode } from '../../services/api/mdtCode/getMDTCode';
import { getCatalogVehiclesByModelId, getQuoteVehicleOptions } from '../../services/api/miles/miles-services';
import { MilesIdentifierSourceType } from '../../services/common/miles-pos-quote-integration/models';
import { assertDefined } from '../../services/common/miscUtils';
import { staticSelector } from '../../services/redux';
import { quoteSelectors } from '../../services/redux/features/quote.slice';

export interface Part {
  code?: string;
  isSelected?: boolean;
  tags?: string[];
}

export interface ConfigurationNode {
  makeCode?: string;
  mdtCode?: string;
  parts?: Part[];
}

export interface Configuration {
  configuration: ConfigurationNode;
}

const getJatoIdFromCatalogVehicles = (
  catalogVehicles: VehicleIntegration.Components.Schemas.GetCatalogVehicleResponse,
  catalogVehicleId: string
) =>
  catalogVehicles?.vehicles
    ?.find((vehicle) =>
      vehicle?.externalVehicleIdentifiers?.some(
        (identifier) =>
          identifier?.type === MilesIdentifierSourceType.MILES_CATALOG_VEHICLE_ID && identifier?.id === catalogVehicleId
      )
    )
    ?.externalVehicleIdentifiers?.find((id) => id?.type === 'JATO')?.id;

const transformBrandToMakeCode = (brand: string) =>
  brand === 'Volkswagen Nutzfahrzeuge' ? 'FR.JATO.VWN' : `FR.JATO.${brand.toUpperCase()}`;

export const buildConfigurationString = async (
  cachedConfiguration?: VWFSAG.Configurator22.Models.IConfigurationResponse
): Promise<Configuration | undefined> => {
  // Configuration already exists
  if (cachedConfiguration) {
    const makeCode = cachedConfiguration?.configuration?.makeCode;
    const mdtCode = cachedConfiguration?.configuration?.mdtCode;
    const parts = (cachedConfiguration?.configuration?.parts ?? [])
      .filter((part) => part?.isSelected === true)
      .map((part) => ({
        code: part.code,
        isSelected: true,
        tags: [],
      }));

    return {
      configuration: {
        makeCode,
        mdtCode,
        parts,
      },
    };
  }

  // Get data from Miles
  const quoteData = assertDefined(staticSelector(quoteSelectors.selectQuote), 'Get quoteData. ');
  const quoteId = assertDefined(staticSelector(quoteSelectors.selectQuoteId), 'Get quoteId. ');
  const catalogVehicleId = assertDefined(staticSelector(quoteSelectors.selectMilesVehicleId), 'Get milesVehicleId.');
  const modelId = assertDefined(staticSelector(quoteSelectors.selectMilesModelId), 'Get modelId.');
  const brand = assertDefined(quoteData?.vehicleData?.model?.manufacturer, 'Get brand.');

  const [vehicleOptions, catalogVehicles] = await Promise.all([
    getQuoteVehicleOptions(quoteId),
    getCatalogVehiclesByModelId(modelId),
  ]);

  const jatoId = assertDefined(getJatoIdFromCatalogVehicles(catalogVehicles, catalogVehicleId), 'Get jatoId.');
  const makeCode = transformBrandToMakeCode(brand);

  let mdtCode = await getMdtCode(jatoId, 'FR');

  // If makeCode includes VWN, replace VOLKSWAGEN in mdtCode with VWN to keep brand consistent
  if (makeCode.includes('VWN') && mdtCode?.includes('VOLKSWAGEN')) {
    mdtCode = mdtCode.replace('VOLKSWAGEN', 'VWN');
  }

  const typeIdsToFind: string[] =
    quoteData?.vehicleData?.model?.options?.map((option) => option?.prKey as string) ?? [];

  const optionsList: string[] =
    vehicleOptions?.vehicleData?.model?.options
      ?.filter((option) => option?.prTypeId && typeIdsToFind.includes(option.prTypeId) && option?.prKey)
      .map((option) => option.prKey as string) ?? [];

  const parts: Part[] = optionsList?.map((code) => ({
    code,
    isSelected: true,
    tags: [],
  }));

  return {
    configuration: {
      makeCode,
      mdtCode,
      parts,
    },
  };
};
